import React, { useState } from "react";
import { chatService } from "../services/apiService";

/**
 * AI bulk-upload popup for the Stock Create page.
 *
 * Purely additive: it uploads a purchase receipt (image / PDF / Excel / CSV),
 * asks the AI service to EXTRACT it into a structured draft, previews what was
 * found, and on confirm hands the draft to the parent via onApply(). It never
 * saves anything itself — the parent injects the rows into the existing Stock
 * Create state and the user reviews + saves through the unchanged flow.
 *
 * Props:
 *  - onApply(draft): called with { distributor, rows, notes } when the user
 *      confirms. The parent is responsible for resolving products and filling
 *      the page state.
 *  - onClose(): close the popup without applying.
 *  - applying (bool): parent is busy applying the draft (resolving products).
 */
const BulkStockUploadModal = ({ onApply, onClose, applying = false }) => {
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [draft, setDraft] = useState(null); // { distributor, rows, notes }
  const [error, setError] = useState("");

  const MAX_BYTES = 10 * 1024 * 1024; // keep in sync with the backend limit

  const onPick = (e) => {
    const f = e.target.files && e.target.files[0];
    setError("");
    setDraft(null);
    if (f && f.size > MAX_BYTES) {
      setFile(null);
      setError("File too large (max 10 MB). Please choose a smaller file.");
      return;
    }
    setFile(f || null);
  };

  const analyze = async () => {
    if (!file) {
      setError("Please choose a file first.");
      return;
    }
    setAnalyzing(true);
    setError("");
    try {
      const data = await chatService.extractStock(file);
      const rows = Array.isArray(data?.rows) ? data.rows : [];
      setDraft({
        distributor: data?.distributor || {},
        rows,
        notes: Array.isArray(data?.notes) ? data.notes : [],
      });
      if (rows.length === 0) {
        setError("No line items could be read from this file. Try a clearer file.");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Could not analyze the file. Please try again.";
      setError(msg);
    } finally {
      setAnalyzing(false);
    }
  };

  const confirm = () => {
    if (draft && draft.rows.length > 0) onApply(draft);
  };

  const d = draft?.distributor || {};
  const fmt = (v) => (v === null || v === undefined || v === "" ? "—" : String(v));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-2xl max-h-[88vh] flex flex-col rounded-xl bg-white shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200">
          <h3 className="flex items-center gap-2 text-base font-bold text-blue-800">
            <i className="fas fa-robot text-indigo-600" /> Bulk Upload by AI
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <i className="fas fa-times" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-auto flex-1 min-h-0">
          {/* Step 1: choose + analyze */}
          <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-4">
            <p className="text-xs text-gray-600 mb-2">
              Upload a distributor bill or stock sheet (image, PDF, Excel or CSV).
              The AI reads it and fills the stock rows for you to review — nothing
              is saved until you click <span className="font-semibold">Save</span> on the page.
            </p>
            <div className="flex items-center gap-2">
              <input
                type="file"
                accept="image/*,application/pdf,.xlsx,.xls,.csv,.txt"
                onChange={onPick}
                className="flex-1 text-sm file:mr-3 file:rounded-full file:border-0 file:bg-indigo-500 file:px-4 file:py-1.5 file:text-white file:text-xs file:font-semibold hover:file:bg-indigo-600"
              />
              <button
                onClick={analyze}
                disabled={!file || analyzing || applying}
                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-white text-xs font-semibold transition ${
                  !file || analyzing || applying
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-indigo-600 hover:to-blue-600"
                }`}
              >
                {analyzing ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-wand-magic-sparkles" />}
                {analyzing ? "Analyzing…" : "Analyze"}
              </button>
            </div>
          </div>

          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-2 text-sm text-red-700">
              <i className="fas fa-triangle-exclamation mr-2" />{error}
            </div>
          )}

          {/* Step 2: preview */}
          {draft && draft.rows.length > 0 && (
            <div className="space-y-3">
              <div className="rounded-lg border border-gray-200 p-3">
                <p className="text-xs font-semibold text-blue-800 mb-1">Distributor / Bill</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-gray-700">
                  <div><span className="text-gray-400">Bill No:</span> {fmt(d.billNo)}</div>
                  <div><span className="text-gray-400">Mobile:</span> {fmt(d.mobileNo)}</div>
                  <div><span className="text-gray-400">Name:</span> {fmt(d.name)}</div>
                  <div><span className="text-gray-400">Date:</span> {fmt(d.billDate)}</div>
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 overflow-hidden">
                <div className="px-3 py-2 bg-gray-50 text-xs font-semibold text-blue-800">
                  {draft.rows.length} item(s) found
                </div>
                <div className="max-h-64 overflow-auto">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-50 text-gray-500 uppercase sticky top-0">
                      <tr>
                        <th className="py-1.5 px-2 text-left">Product</th>
                        <th className="py-1.5 px-2 text-left">Batch</th>
                        <th className="py-1.5 px-2 text-right">Qty</th>
                        <th className="py-1.5 px-2 text-right">MRP</th>
                        <th className="py-1.5 px-2 text-right">Purchase</th>
                        <th className="py-1.5 px-2 text-left">Expiry</th>
                      </tr>
                    </thead>
                    <tbody>
                      {draft.rows.map((r, i) => (
                        <tr key={i} className={i % 2 ? "bg-gray-50" : "bg-white"}>
                          <td className="py-1 px-2">{fmt(r.productName)}</td>
                          <td className="py-1 px-2">{fmt(r.batchNo)}</td>
                          <td className="py-1 px-2 text-right">{fmt(r.quantity)}</td>
                          <td className="py-1 px-2 text-right">{fmt(r.mrp)}</td>
                          <td className="py-1 px-2 text-right">{fmt(r.purchasePrice)}</td>
                          <td className="py-1 px-2">{fmt(r.expiryDate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {draft.notes.length > 0 && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                  <p className="font-semibold mb-1">Please review:</p>
                  <ul className="list-disc ml-4 space-y-0.5">
                    {draft.notes.map((n, i) => <li key={i}>{n}</li>)}
                  </ul>
                </div>
              )}

              <p className="text-xs text-gray-400">
                Products not already in your catalog will be created when you apply.
                You can edit every value on the page before saving.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 bg-white">
          <button
            onClick={onClose}
            disabled={applying}
            className="px-4 py-1.5 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50 text-xs font-semibold disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={confirm}
            disabled={!draft || draft.rows.length === 0 || analyzing || applying}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-white text-xs font-semibold transition ${
              !draft || draft.rows.length === 0 || analyzing || applying
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-gradient-to-r from-green-500 to-emerald-600 hover:from-emerald-600 hover:to-green-700"
            }`}
          >
            {applying ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-check" />}
            {applying ? "Adding…" : "Add to page"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkStockUploadModal;
